// Check Availability Application Configuration
export const appConfig = {
  // App Information
  app: {
    name: "Check Availability",
    version: "1.0.0",
    description: "Check room availability for your stay",
    tagline: "Find available rooms for your dates",
    port: 3008,
    devUrl: "http://localhost:3008",
  },

  // Header Configuration
  header: {
    logo: "Check Availability",
    navigation: [
      { label: "Features", href: "#features" },
      { label: "About", href: "#about" },
    ],
    ctaButton: {
      text: "Get Yours Now →",
      href: "/admin",
    },
  },

  // Hero Section
  hero: {
    title: "Check Room Availability",
    subtitle: "Find available rooms for your preferred dates",
    buttons: [
      {
        text: "Check Availability",
        type: "primary",
        href: "#check-availability",
      },
      {
        text: "Learn More",
        type: "secondary",
        href: "#learn-more",
      },
    ],
  },

  // Features Section
  features: {
    title: "Why Choose Check Availability?",
    items: [
      {
        icon: "📅",
        title: "Easy Date Selection",
        description: "Simple calendar interface to pick your dates",
      },
      {
        icon: "🔍",
        title: "Real-time Results",
        description: "Instant availability checking with live data",
      },
      {
        icon: "📱",
        title: "Mobile Friendly",
        description: "Works perfectly on all devices and screen sizes",
      },
      {
        icon: "⚡",
        title: "Fast & Reliable",
        description: "Quick results with reliable availability data",
      },
    ],
  },

  // Date Picker Configuration
  datePicker: {
    title: "Select Date Range",
    placeholder: "Select date range...",
    format: "MMM dd, yyyy",
    clearButtonText: "✕",
    applyButtonText: "Apply",
    weekdays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    // Multiple Property BBIDs for availability checking
    propertyBBIDs: ["20001"],
  },

  // Footer
  footer: {
    copyright: "© 2025 Check Availability. All rights reserved.",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
      { label: "Contact", href: "/contact" },
    ],
  },

  // Theme Configuration
  theme: {
    colors: {
      primary: {
        gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        start: "#667eea",
        end: "#764ba2",
      },
      secondary: {
        gradient: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
        start: "#2c3e50",
        end: "#34495e",
      },
      accent: {
        gradient: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
        start: "#e74c3c",
        end: "#c0392b",
      },
    },
    fonts: {
      primary: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    },
    spacing: {
      section: "4rem",
      container: "1200px",
    },
  },

  // API Configuration
  api: {
    baseUrl: process.env.REACT_APP_API_URL || "https://api.checkavailability.com",
    timeout: 10000,
    endpoints: {
      auth: "/auth",
      users: "/users",
      analytics: "/analytics",
    },
    // Cloudflare Worker for availability checking
    availabilityWorker: "https://checkavailability.alldigital.workers.dev",
    // Cloudflare Worker for content data
    contentWorker: "https://getcontent.alldigital.workers.dev",
    // Cloudflare Worker for room images
    imageWorker: "https://getimages.alldigital.workers.dev",
  },

  // Booking Configuration
  booking: {
    propertyId: "29515", // Use the same BBID that works for availability
    nbid: "371",
    baseUrl: "https://book.nightsbridge.com"
  },

  // Widget Configuration
  widget: {
    productionUrl: "https://nbsaas.pages.dev/", // Replace with your actual domain
    developmentUrl: "http://localhost:3334"
  },

  // Feature Flags
  featureFlags: {
    enableAnalytics: true,
    enableNotifications: true,
    enableDarkMode: false,
    enableMultiLanguage: false,
  },

  // Social Media Links
  social: {
    twitter: "https://twitter.com/checkavailability",
    linkedin: "https://linkedin.com/company/checkavailability",
    github: "https://github.com/checkavailability",
  },

  // Contact Information
  contact: {
    email: "hello@checkavailability.com",
    phone: "+1 (555) 123-4567",
    address: "123 Availability Street, Tech City, TC 12345",
  },

  // Pricing Plans (for future use)
  pricing: {
    plans: [
      {
        name: "Starter",
        price: "$29",
        period: "month",
        features: ["Basic Analytics", "5 Projects", "Email Support"],
        popular: false,
      },
      {
        name: "Professional",
        price: "$99",
        period: "month",
        features: ["Advanced Analytics", "Unlimited Projects", "Priority Support"],
        popular: true,
      },
      {
        name: "Enterprise",
        price: "Custom",
        period: "month",
        features: ["Custom Analytics", "White-label", "Dedicated Support"],
        popular: false,
      },
    ],
  },
};

// Helper functions for config
export const getConfig = (path) => {
  return path.split('.').reduce((obj, key) => obj?.[key], appConfig);
};

export const updateConfig = (path, value) => {
  const keys = path.split('.');
  const lastKey = keys.pop();
  const target = keys.reduce((obj, key) => obj[key], appConfig);
  if (target) {
    target[lastKey] = value;
  }
};

export default appConfig; 